import { FunctionComponent, useCallback, useEffect, useRef, useState } from "react";
import { IonContent, IonFab, IonFabButton, IonSpinner } from "@ionic/react";
import { AnnotationModel, AnnotationPosition } from "../models/annotation.model";
import { loadSvg, scaleSvgAnnotations, updateSvgAnnotations } from "../../svgLib/utils";
import { PlanPresenter } from "../presenters/plan.presenter";
import { useTranslation } from "react-i18next";

import "./map.component.scss";
import annotationIcon from "../../../icons/annotation.svg";

interface SvgViewerProps {
  svgContent: string;
  planId: string;
  annotations: AnnotationModel[];
  equipmentIdSelected: string;
  onCreateAnnotation: (annotationPosition: AnnotationPosition) => void;
  onEditAnnotation: (annotation: AnnotationModel) => void;
  saveClickedEquipmentId: (equipmentId: string) => void;
  saveClickedPosition: (annotationPosition: AnnotationPosition) => void;
  saveClickedName: (name: string) => void;
  saveClickedElementType: (type: string) => void;
  style: Record<string, string>;
  zoomCoordinates?: {x: number, y: number} | null;
  onZoomComplete?: () => void;
}

const SvgViewer: FunctionComponent<SvgViewerProps> = ({
  svgContent,
  planId,
  annotations,
  equipmentIdSelected,
  onCreateAnnotation,
  onEditAnnotation,
  saveClickedEquipmentId,
  saveClickedName,
  saveClickedPosition,
  saveClickedElementType,
  style,
  zoomCoordinates,
  onZoomComplete,
}) => {
  const { t } = useTranslation();
  const rootElementRef = useRef<HTMLDivElement>(null);
  const [svgLoaded, setSvgLoaded] = useState(false);

  const annotationClickHandler = useCallback(
    (annotation: AnnotationModel) => {
      onEditAnnotation(annotation);
    },
    [onEditAnnotation]
  );

  const [singleTapMode, setSingleTapMode] = useState(false);

  useEffect(() => {
    loadSvg(rootElementRef.current!, svgContent);
  }, [svgContent]);

  useEffect(() => {
    updateSvgAnnotations(rootElementRef.current!, annotations, annotationClickHandler);
  }, [svgContent, annotations, annotationClickHandler]);

  useEffect(() => {
    if (!equipmentIdSelected) {
      InfoTip(false);
      return;
    }
    Select(equipmentIdSelected, true);
    return () => {
      Select(equipmentIdSelected, false);
    };
  }, [equipmentIdSelected]);

  oListener.SvgCurViewBoxChange = () => {
    scaleSvgAnnotations(rootElementRef.current!);
  };

  oListener.Click = (id, type, stype, codebib, numCompPere, text, pere, x, y) => {
    const annotationPosition = new AnnotationPosition(id || planId, x, -y);
    saveClickedPosition(annotationPosition);
    saveClickedName(text || t("plan.annotations.name.facade"));
    saveClickedEquipmentId(id !== equipmentIdSelected ? id : "");
    saveClickedElementType(type || "");
    if (singleTapMode) {
      onCreateAnnotation(annotationPosition);
    }
  };

  oListener.LongPress = (numComp, targetType, stype, codebib, numCompPere, text, pere, x, y) => {
    const annotationPosition = new AnnotationPosition(numComp || planId, x, -y);
    onCreateAnnotation(annotationPosition);
  };

  // Ajouter l'écouteur pour détecter quand le SVG est complètement chargé
  useEffect(() => {
    const originalSvgLoadEnd = oListener?.SvgLoadEnd;
    
    oListener.SvgLoadEnd = (type: string, nSit?: number, nBat?: number, nEta?: number) => {
      setSvgLoaded(true);
      
      // Appeler l'original si il existe
      if (originalSvgLoadEnd) {
        originalSvgLoadEnd(type, nSit, nBat, nEta);
      }
    };

    return () => {
      // Restaurer l'original au démontage
      if (originalSvgLoadEnd) {
        oListener.SvgLoadEnd = originalSvgLoadEnd;
      }
    };
  }, []);

  // Fallback: vérifier périodiquement si le SVG est chargé (au cas où SvgLoadEnd ne fonctionne pas)
  useEffect(() => {
    if (!svgLoaded && rootElementRef.current) {
      const checkSvgLoaded = () => {
        const svgElement = rootElementRef.current?.querySelector('svg');
        const dessinElement = svgElement?.querySelector('#dessin');
        const zoomFunction = (window as any).ZoomOn;
        
        if (svgElement && dessinElement && typeof zoomFunction === 'function') {
          setSvgLoaded(true);
        }
      };

      // Vérifier immédiatement
      checkSvgLoaded();
      
      // Vérifier périodiquement pendant 5 secondes maximum
      const interval = setInterval(checkSvgLoaded, 100);
      const timeout = setTimeout(() => {
        clearInterval(interval);
      }, 5000);

      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    }
  }, [svgLoaded]);


  useEffect(() => {
    if (zoomCoordinates && svgLoaded && rootElementRef.current) {
      const svgElement = rootElementRef.current.querySelector('svg');
      const zoomFunction = (window as any).ZoomOn;

      
      if (svgElement && typeof zoomFunction === 'function') {
        try {
          // Créer un élément temporaire avec un ID spécifique
          const tempId = 'temp-annotation-zoom';
          const existingTemp = svgElement.querySelector(`#${tempId}`);
          if (existingTemp) existingTemp.remove();
          
          const tempElement = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
          tempElement.setAttribute('id', tempId);
          tempElement.setAttribute('cx', zoomCoordinates.x.toString());
          tempElement.setAttribute('cy', zoomCoordinates.y.toString());
          tempElement.setAttribute('r', '300');
          tempElement.setAttribute('fill', 'transparent');
          tempElement.setAttribute('stroke', 'none');
          
          const dessinElement = svgElement.querySelector('#dessin');
          if (dessinElement) {
            dessinElement.appendChild(tempElement);
            
            zoomFunction(tempId, true);
            
            setTimeout(() => {
              const tempToRemove = svgElement.querySelector(`#${tempId}`);
              if (tempToRemove) tempToRemove.remove();
            }, 1000);
          }
          
          if (onZoomComplete) setTimeout(onZoomComplete, 100);
        } catch (error) {
          console.error('Erreur lors du zoom:', error);
          if (onZoomComplete) onZoomComplete();
        }
      } else {
        if (onZoomComplete) onZoomComplete();
      }
    }
  }, [zoomCoordinates, svgLoaded, onZoomComplete]);

  return (
    <IonContent>
      <div id="svg-container" ref={rootElementRef} style={style} />
      <IonFab vertical="bottom" horizontal="end" slot="fixed">
        <IonFabButton
          onClick={() => setSingleTapMode(!singleTapMode)}
          color={singleTapMode ? "secondary" : "medium"}
          size="small"
        >
          <img src={annotationIcon} alt="" style={{ height: "20px" }} />
        </IonFabButton>
      </IonFab>
    </IonContent>
  );
};

interface MapProps {
  planId: string;
  planSVG: PlanPresenter | null;
  annotations: AnnotationModel[];
  equipmentIdSelected: string;
  onCreateAnnotation: (annotationPosition: AnnotationPosition) => void;
  onEditAnnotation: (annotation: AnnotationModel) => void;
  saveClickedEquipmentId: (equipmentId: string) => void;
  saveClickedPosition: (annotationPosition: AnnotationPosition) => void;
  saveClickName: (name: string) => void;
  saveClickedElementType: (type: string) => void;
  zoomCoordinates?: {x: number, y: number} | null;
  onZoomComplete?: () => void;
}

export const Map: FunctionComponent<MapProps> = ({
  planId,
  planSVG,
  annotations,
  onCreateAnnotation,
  onEditAnnotation,
  equipmentIdSelected,
  saveClickedEquipmentId,
  saveClickedPosition,
  saveClickName,
  saveClickedElementType,
  zoomCoordinates,
  onZoomComplete,
}) =>
  planSVG === null ? (
    <IonSpinner role="progressbar" name="crescent" data-testid="progressbar" style={{ height: "100%", width: "100%" }} />
  ) : (
    <SvgViewer
      svgContent={planSVG.inlineSvg}
      planId={planId}
      annotations={annotations}
      equipmentIdSelected={equipmentIdSelected}
      onCreateAnnotation={onCreateAnnotation}
      onEditAnnotation={onEditAnnotation}
      saveClickedEquipmentId={saveClickedEquipmentId}
      saveClickedPosition={saveClickedPosition}
      saveClickedName={saveClickName}
      saveClickedElementType={saveClickedElementType}
      style={{ height: "100%", display: "flex" }}
      zoomCoordinates={zoomCoordinates}
      onZoomComplete={onZoomComplete}
    />
  );
